import AES from 'crypto-js/aes.js';
import Utf8 from 'crypto-js/enc-utf8.js';
import Base64 from 'crypto-js/enc-base64.js';
import provideEncryptKey from './provideEncryptKey.js';

// CryptoJS rejects unpaired surrogates; TextEncoder would replace them with U+FFFD.
// Preserve the legacy error instead of silently encrypting different text.
const unpairedSurrogate = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(^|[^\uD800-\uDBFF])[\uDC00-\uDFFF]/;

// Keep the existing wire format: UTF-8, AES-CBC/PKCS7, the same key/IV, raw Base64.
// Start importing immediately; collection can run while the key is prepared.
// The imported key is shared by the fingerprint and actions within one run.
export default function createEncryptor(window) {
    let keyPromise;
    let encoder;
    let iv;
    let subtle;

    try {
        subtle = window.crypto?.subtle;
        if (subtle && typeof window.TextEncoder === 'function' && typeof window.btoa === 'function') {
            encoder = new window.TextEncoder();
            iv = encoder.encode(provideEncryptKey());
            // Handle rejection now, even if collection aborts before encryption.
            keyPromise = Promise.resolve(subtle.importKey('raw', iv, 'AES-CBC', false, ['encrypt'])).catch(() => null);
        }
    } catch {
        // A synchronous initialization error also leaves CryptoJS available.
    }

    return async data => {
        try {
            if (keyPromise && !unpairedSurrogate.test(data)) {
                const key = await keyPromise;
                if (key) {
                    const ciphertext = await subtle.encrypt({name: 'AES-CBC', iv}, key, encoder.encode(data));
                    const bytes = new Uint8Array(ciphertext);
                    if (typeof bytes.toBase64 === 'function') {
                        return bytes.toBase64();
                    }
                    let binary = '';
                    // Bound the argument count for large fingerprints in older browsers.
                    for (let offset = 0; offset < bytes.length; offset += 32768) {
                        binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
                    }
                    return window.btoa(binary);
                }
            }
        } catch {
            // Unavailable/rejected native crypto must preserve the legacy behavior.
        }

        const key = Utf8.parse(provideEncryptKey());
        return Base64.stringify(AES.encrypt(data, key, {iv: key}).ciphertext);
    };
}
